import React, { useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Area } from 'react-easy-crop';
import { toast } from 'react-toastify';

import classNames from 'classnames';

import ImageProxy from '@next-image-proxy';

import { ConfirmModal, useModalController } from '@uikit';

import { useUploadFileMutation } from '@query';

import CropModal from '@uikit/components/CropModal/CropModal';
import { AddImage } from '@uikit/icons/AddImage';
import { Delete } from '@uikit/icons/Delete';

import { getCroppedImg } from '@shared/helpers/image';

interface ProfileBannerProps {
  className?: string;
  isEditable?: boolean;
  onUpdate?: (fileHash: string) => void;
  onDelete?: () => void;
  width: number;
  height: number;
  src?: string | null;
}

const BYTES_IN_MB = 1e6;
const DEFAULT_SRC = 'users/59b5730de09cc762.png';
const BANNER_MAX_SIZE_IN_MB = 10;

const defaultAccept = {
  'image/jpg': ['.jpeg', '.png', '.jpg'],
};

const ProfileBanner = ({
  className,
  isEditable,
  width,
  height,
  src,
  onUpdate,
  onDelete,
  ...props
}: ProfileBannerProps) => {
  const cropModal = useModalController();
  const [fileUrl, setFileUrl] = useState<string>();
  const { mutateAsync: uploadFile, isPending: isUploading } = useUploadFileMutation();
  const { getInputProps, open, acceptedFiles } = useDropzone({
    multiple: false,
    accept: defaultAccept,
  });
  const confirmDeleteModal = useModalController();
  const [file] = acceptedFiles;

  const handleCrop = async (croppedArea: Area, croppedAreaPixels: Area) => {
    if (!fileUrl || !file) {
      return;
    }

    const croppedImgFile = await getCroppedImg(fileUrl, croppedAreaPixels);

    if (!croppedImgFile) {
      return;
    }

    const preparedFile = new File([croppedImgFile], file.name, {
      type: 'image/jpeg',
    });

    const { hash } = await uploadFile(preparedFile);

    if (onUpdate) {
      onUpdate(hash);
    }

    cropModal.close();
  };

  useEffect(() => {
    if (!file) {
      return;
    }

    const fileSizeInMB = file?.size / BYTES_IN_MB;
    const hasMaxSizeError = fileSizeInMB > BANNER_MAX_SIZE_IN_MB;

    if (hasMaxSizeError) {
      toast.error(
        <>
          File is too big <b>{fileSizeInMB.toFixed(2)}</b> MB. Max file size is{' '}
          <b>{BANNER_MAX_SIZE_IN_MB.toFixed(2)} MB</b>
        </>,
      );

      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      if (typeof reader.result !== 'string') {
        return;
      }

      setFileUrl(reader.result);
      cropModal.open();
    };

    reader.readAsDataURL(file);
  }, [file]);

  return (
    <div className={classNames('relative', className)} {...props}>
      <ImageProxy
        src={src || DEFAULT_SRC}
        alt="Banner"
        className="w-full aspect-[1650/280]"
        width={width}
        height={height}
      />

      {isEditable && (
        <>
          <input {...getInputProps()} />

          <CropModal
            url={fileUrl}
            onCrop={handleCrop}
            isLoading={isUploading}
            aspect={1650 / 280}
            cropShape="rect"
            {...cropModal.props}
          />

          <div className="flex justify-center items-center gap-6 absolute top-0 left-0 w-full h-full bg-neutral-600/70 opacity-0 focus-within:opacity-100 hover:opacity-100 transition-opacity">
            <button
              onClick={open}
              className="flex justify-center items-center w-[72px] h-[72px] border border-neutral-50 rounded-full text-neutral-50"
            >
              <AddImage className="icon" />
            </button>
            {src && (
              <button
                onClick={confirmDeleteModal.open}
                className="flex justify-center items-center w-[72px] h-[72px] border border-neutral-50 rounded-full text-neutral-50"
              >
                <Delete className="icon" />
              </button>
            )}
          </div>

          <ConfirmModal
            heading="Confirm Cover Image Deletion"
            description="Are you sure you want to delete your current profile cover image? This action cannot be undone. Confirming will remove the existing cover image from your profile."
            onConfirm={() => {
              if (onDelete) {
                onDelete();
              }

              confirmDeleteModal.close();
            }}
            controller={confirmDeleteModal}
            classnames={{
              modal: 'w-full m-auto !max-w-[450px] p-5 sm:p-10',
            }}
          />
        </>
      )}
    </div>
  );
};

export default ProfileBanner;
